import {
	FormEvent,
	HTMLAttributes,
	useCallback,
	useContext,
	useEffect,
	useState,
} from 'react';

import Link from 'next/link';
import { useRouter } from 'next/router';
import { func } from 'prop-types';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';

import { Button } from '@/components/Atoms/Button/Button';
import { Checkbox } from '@/components/Atoms/Checkbox/Checkbox';
import { InputField } from '@/components/Atoms/InputField/InputField';
import { SystemMessage } from '@/components/Atoms/SystemMessage';
import Password from '@/components/Molecules/Password';
import { Policies } from '@/components/Molecules/Policies';
import { LocalesContext } from '@/context/locales/LocalesProvider';
import UserContext from '@/context/user';
import useAlert from '@/hooks/useAlert';
import useEmailValidation from '@/hooks/validation/useEmailValidation';
import useNameValidation from '@/hooks/validation/useNameValidation';
import usePasswordValidation from '@/hooks/validation/usePasswordValidation';
import getPasswordValidation from '@/hooks/validation/usePasswordValidation/getPasswordValidation';
import { classMerge } from '@/utils/class-names';
import updateInputValue from '@/utils/inputs/updateInputValue';
import { interpolateNodes } from '@/utils/Interpolate';
import { makeLinkAbsolute } from '@/utils/Slug';
import { Routes } from 'data/Routes';

export interface RegisterFormProps extends HTMLAttributes<HTMLFormElement> {
	onSuccess?: () => void;
}

/**
 * RegisterForm Library Component
 */
export const RegisterForm = ({
	className,
	onSuccess,
	...props
}: RegisterFormProps) => {
	const { locales } = useContext(LocalesContext);
	const { setAlert } = useAlert();
	const { executeRecaptcha } = useGoogleReCaptcha();
	const { registerUser } = useContext(UserContext);

	const router = useRouter();

	const [password, setPassword] = useState('');
	const [firstName, setFirstName] = useState('');
	const [lastName, setLastName] = useState('');
	const [recaptchaToken, setRecaptchaToken] = useState('');
	const [apiAuthMessage, setApiAuthMessage] = useState<string | undefined>();
	const [isMarketingConsent, setIsMarketingConsent] = useState(true);
	const [areTermsAccepted, setAreTermsAccepted] = useState(false);
	const [email, setEmail] = useState(
		decodeURIComponent((router?.query?.email || '') as string) || ''
	);

	const { isEmailValid, emailValidationMessage } = useEmailValidation(email);
	const isFirstNameValid = useNameValidation(firstName, 'First Name');
	const isLastNameValid = useNameValidation(lastName, 'Last Name');
	const passwordValidationCriteria = usePasswordValidation(password);
	const passwordValidation = getPasswordValidation(passwordValidationCriteria);

	const isPasswordValid = !Object.values(passwordValidationCriteria).some(
		(criteria) => !Boolean(criteria)
	);

	const isFormValid =
		isEmailValid &&
		isFirstNameValid &&
		isLastNameValid &&
		isPasswordValid &&
		areTermsAccepted;

	const [isSignUpRequestInProgress, setIsSignUpRequestInProgress] =
		useState(false);

	const handleReCaptchaVerify = useCallback(async () => {
		if (!executeRecaptcha) {
			return;
		}

		try {
			const token = await executeRecaptcha('LOGIN');

			setRecaptchaToken(token);
		} catch (err) {
			setAlert((err as Error).message, 'error');
		}
	}, [executeRecaptcha]);

	useEffect(() => {
		handleReCaptchaVerify();
	}, [handleReCaptchaVerify]);

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		try {
			event.preventDefault();

			setIsSignUpRequestInProgress(true);

			await registerUser({
				email,
				firstName,
				lastName,
				password,
				areTermsAccepted: areTermsAccepted,
				isMarketingConsent: isMarketingConsent,
				token: recaptchaToken,
				action: 'LOGIN',
			});

			setAlert(locales['register-success'], 'success');
			router.push(`${Routes.verifyAccountEmailSent}?email=${email}`);

			setIsSignUpRequestInProgress(false);
		} catch (err) {
			setIsSignUpRequestInProgress(false);
			setApiAuthMessage((err as Error).message);
		}
	};

	return (
		<form
			data-cy="RegisterForm"
			{...props}
			className={classMerge('flex flex-col gap-y-2 w-full', className)}
			onSubmit={handleSubmit}
		>
			<InputField
				autoComplete="email"
				isValid={isEmailValid}
				type="email"
				name={locales.email}
				value={email}
				onChange={(event) => updateInputValue(event, setEmail)}
			/>

			{(emailValidationMessage || apiAuthMessage) && (
				<SystemMessage type="error">
					{apiAuthMessage ? apiAuthMessage : emailValidationMessage}
				</SystemMessage>
			)}

			<InputField
				autoComplete="given-name"
				isValid={isFirstNameValid}
				type="text"
				name={locales['first-name']}
				value={firstName}
				onChange={(event) => updateInputValue(event, setFirstName)}
			/>

			<InputField
				autoComplete="family-name"
				isValid={isLastNameValid}
				type="text"
				name={locales['last-name']}
				value={lastName}
				onChange={(event) => updateInputValue(event, setLastName)}
			/>

			<Password
				isNewPassword
				criteria={passwordValidation}
				value={password}
				onChange={(event) => updateInputValue(event, setPassword)}
			/>

			<Checkbox
				checked={isMarketingConsent}
				id="updateMe"
				name="updateMe"
				className="my-5 text-body-3 text-neutral-grey-90"
				onChange={() => setIsMarketingConsent(!isMarketingConsent)}
			>
				{locales['consent-email-me']}
			</Checkbox>

			<Checkbox
				checked={areTermsAccepted}
				id="agreement"
				name="agreement"
				className="my-5 text-body-3 text-neutral-grey-90"
				onChange={() => setAreTermsAccepted(!areTermsAccepted)}
			>
				<div>
					{interpolateNodes(
						locales['agree-to-terms'],
						<Link
							href={Routes.privacyPolicy}
							as={makeLinkAbsolute(Routes.privacyPolicy)}
							key="1"
						>
							<a className="text-neutral-black">
								{locales['privacy-and-cookies']}
							</a>
						</Link>,
						<Link
							href={Routes.termsConditions}
							as={makeLinkAbsolute(Routes.termsConditions)}
							key="2"
						>
							<a className="text-neutral-black">{locales['user-agreement']}</a>
						</Link>
					)}
				</div>
			</Checkbox>

			<Policies className="mt-2 mb-5 font-body text-body-3 font-bold text-left text-neutral-grey-90" />

			<Button
				size="large"
				className={classMerge('btn-primary')}
				type="submit"
				disabled={!isFormValid || isSignUpRequestInProgress}
				isLoading={isSignUpRequestInProgress}
			>
				{locales.join}
			</Button>
		</form>
	);
};

RegisterForm.propTypes = {
	onSuccess: func,
};
